import type { ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warning';

export interface ToastOptions {
  id?: string;
  durationMs?: number;
}

export interface ToastItem {
  id: string;
  type: ToastType;
  message: ReactNode;
  durationMs: number;
  isExiting: boolean;
}

const DEFAULT_DURATION_MS = 2400;
const EXIT_ANIMATION_MS = 180;
const MAX_TOASTS = 3;

let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
const timeouts = new Map<string, number[]>();

const notify = () => {
  listeners.forEach((l) => l());
};

export const subscribeToasts = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getToastSnapshot = () => toasts;

const clearTimers = (id: string) => {
  const ids = timeouts.get(id);
  if (!ids) return;

  ids.forEach((timeoutId) => window.clearTimeout(timeoutId));
  timeouts.delete(id);
};

const setTimers = (id: string, timerIds: number[]) => {
  clearTimers(id);
  timeouts.set(id, timerIds);
};

const startExit = (id: string) => {
  const toast = toasts.find((t) => t.id === id);
  if (!toast || toast.isExiting) return;

  toasts = toasts.map((t) => (t.id === id ? { ...t, isExiting: true } : t));
  notify();

  const removeId = window.setTimeout(() => {
    clearTimers(id);
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  }, EXIT_ANIMATION_MS);

  setTimers(id, [removeId]);
};

const addToast = (
  type: ToastType,
  message: ReactNode,
  options?: ToastOptions,
) => {
  const id =
    options?.id ??
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const durationMs = options?.durationMs ?? DEFAULT_DURATION_MS;

  const item: ToastItem = {
    id,
    type,
    message,
    durationMs,
    isExiting: false,
  };

  if (toasts.length >= MAX_TOASTS) {
    const overflow = toasts.length - MAX_TOASTS + 1;
    const toRemove = toasts.slice(0, overflow);
    toRemove.forEach((t) => {
      clearTimers(t.id);
    });
    toasts = toasts.slice(overflow);
  }

  toasts = [...toasts, item];
  notify();

  if (durationMs > 0) {
    const timeoutId = window.setTimeout(() => {
      startExit(id);
    }, durationMs);
    setTimers(id, [timeoutId]);
  }

  return id;
};

export const toast = {
  success: (message: ReactNode, options?: ToastOptions) =>
    addToast('success', message, options),
  error: (message: ReactNode, options?: ToastOptions) =>
    addToast('error', message, options),
  warning: (message: ReactNode, options?: ToastOptions) =>
    addToast('warning', message, options),
  dismiss: (id?: string) => {
    if (id) {
      startExit(id);
      return;
    }

    toasts.forEach((t) => {
      startExit(t.id);
    });
  },
  clear: () => {
    toasts.forEach((t) => clearTimers(t.id));
    toasts = [];
    notify();
  },
};

export const showErrorToast = (message: ReactNode, options?: ToastOptions) => {
  return addToast('error', message, options);
};
